/**
 * Normalize campus/term strings for comparison (matches CMRP sidebar / URL state usage).
 * @param {string} value
 * @returns {string}
 */
function normalizeCmrpKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * Resolve campus rental grace period and term rental refund date from window.courseData.
 * Uses ddcs_campus_entity.rental_refund_grace_period and ddcs_term.rental_refund_date.
 *
 * @param {string} campusName - Selected campus display name
 * @param {string} termName - Selected term display name (or code)
 * @param {string} [termId] - DDCS term_id when available (preferred)
 * @returns {{ _rental_refund_grace_period: string, _rental_refund_date: string }}
 */
function getCmrpRentalCampusTermProperties(campusName, termName, termId) {
  /** @type {{ _rental_refund_grace_period: string, _rental_refund_date: string }} */
  const empty = { _rental_refund_grace_period: '', _rental_refund_date: '' };
  // @ts-ignore
  const cd = typeof window !== 'undefined' ? window.courseData : null;
  if (!cd || !Array.isArray(cd.ddcs_campus_entity) || !Array.isArray(cd.ddcs_term)) {
    return empty;
  }

  const norm = normalizeCmrpKey;
  /** @type {any} */
  const campus = cd.ddcs_campus_entity.find(
    /** @param {any} c */ c => norm(c.name) === norm(campusName)
  );

  /** @type {any} */
  let term = null;
  const tid = termId != null && String(termId).trim() !== '' ? String(termId).trim() : '';
  if (tid) {
    term = cd.ddcs_term.find(
      /** @param {any} t */ t =>
        String(t.term_id) === tid || norm(t.term_id) === norm(tid)
    );
  }
  if (!term && termName) {
    term = cd.ddcs_term.find(
      /** @param {any} t */ t =>
        norm(t.name) === norm(termName) ||
        (t.term != null && norm(t.term) === norm(termName))
    );
  }

  const grace = campus?.rental_refund_grace_period;
  const refundDate = term?.rental_refund_date;

  return {
    _rental_refund_grace_period: grace != null && String(grace) !== '' ? String(grace) : '',
    _rental_refund_date:
      refundDate != null && String(refundDate) !== '' ? String(refundDate) : '',
  };
}

/**
 * SOLID Product Handler - Main facade for product system
 */
class SOLIDProductHandler {
  /**
   * @param {any} options - Configuration options
   */
  constructor(options = {}) {
    this.products = options.products || [];
    this.ddcsSection = options.ddcsSection || [];

    this.variantDetector =
      options.variantDetector ||
      // @ts-ignore
      new window.ProductSystem.ShopifyVariantDetector();
    this.infoExtractor =
      // @ts-ignore
      options.infoExtractor || new window.ProductSystem.ShopifyInfoExtractor();
    this.priceCalculator =
      options.priceCalculator ||
      // @ts-ignore
      new window.ProductSystem.ShopifyPriceCalculator();
    this.htmlRenderer =
      options.htmlRenderer ||
      // @ts-ignore
      new window.ProductSystem.ShopifyHTMLRenderer(
        this.variantDetector,
        this.priceCalculator
      );
    this.adoptionManager =
      options.adoptionManager ||
      // @ts-ignore
      new window.ProductSystem.AdoptionStatusManager();

    // Bind methods for callbacks
    this.addSelectedProductsToCart = this.addSelectedProductsToCart.bind(this);
    this.handleNotifyMe = this.handleNotifyMe.bind(this);
  }

  // Variant detection
  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has digital variants
   */
  hasDigitalVariants(product) {
    return this.variantDetector.hasDigitalVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has print variants
   */
  hasPrintVariants(product) {
    return this.variantDetector.hasPrintVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has rental variants
   */
  hasRentalVariants(product) {
    return this.variantDetector.hasRentalVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has buy variants
   */
  hasBuyVariants(product) {
    return this.variantDetector.hasBuyVariants(product);
  }

  // Information extraction
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Author name
   */
  extractAuthorName(product) {
    return this.infoExtractor.extractAuthorName(product);
  }

  /**
   * @param {string} title - Product title
   * @returns {string|null} Edition
   */
  extractEditionFromTitle(title) {
    return this.infoExtractor.extractEditionFromTitle(title);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} ISBN
   */
  extractISBNFromProduct(product) {
    return this.infoExtractor.extractISBNFromProduct(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublisherName(product) {
    return this.infoExtractor.extractPublisherName(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublisher(product) {
    return this.infoExtractor.extractPublisher(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractFormat(product) {
    return this.infoExtractor.extractFormat(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractCopyRight(product) {
    return this.infoExtractor.extractCopyRight(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublication(product) {
    return this.infoExtractor.extractPublication(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Published date
   */
  extractPublishedDate(product) {
    return this.infoExtractor.extractPublishedDate(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher code
   */
  extractPublisherCode(product) {
    return this.infoExtractor.extractPublisherCode(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} MTC Title ID
   */
  extractMTCTitleId(product) {
    return this.infoExtractor.extractMTCTitleId(product);
  }

  // Price calculation
  /**
   * @param {Product} product - Product object
   * @param {string} format - Format
   * @returns {string|null} Lowest price
   */
  getLowestPriceForFormat(product, format) {
    return this.priceCalculator.getLowestPriceForFormat(product, format);
  }

  /**
   * @param {Product} product - Product object
   * @returns {Object|null} Price range
   */
  calculatePriceRange(product) {
    return this.priceCalculator.calculatePriceRange(product);
  }

  // HTML generation
  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  generateFormatButtons(product) {
    return this.htmlRenderer.generateFormatButtons(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   * @param {any} type
   */
  async generateInitialPurchaseOptions(type,product,sectionData) {
    return await this.htmlRenderer.generatePurchaseOptions(type,product,sectionData);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  getVariantUniques(product) {
    return this.htmlRenderer._getFormats(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  async generatePurchaseOptions(type,product) {
    return await this.htmlRenderer.generatePurchaseOptions(type,product);
  }

  // Adoption status
  /**
   * @param {string} note - Note text
   * @returns {StatusInfo} Status info
   */
  getAdoptionStatusInfo(note) {
    return this.adoptionManager.getStatusInfo(note);
  }

  /**
   * @param {Filter} filter - Filter object
   * @param {Section} section - Section object
   * @param {string} courseCode - Course code (unused but kept for compatibility)
   * @param {string} note - Note text
   * @param {Object} statusInfo - Status info object (unused but kept for compatibility)
   * @returns {string} HTML
   */
  // @ts-ignore
  createAdoptionCardHTML(filter, section, courseCode, note, statusInfo) {
    return this.adoptionManager.createAdoptionCard(filter, section, note).html;
  }

  // Cart functionality
  async addSelectedProductsToCart() {
    /** @type {Array<Object>} */
    const selectedProducts = [];
    const checkboxes = document.querySelectorAll('.purchase-checkbox:checked');
    
    // Helper to extract variant ID
    const extractVariantId = (gid) => {
      if (!gid) return null;
      if (typeof gid !== 'string') return String(gid);
      const parts = gid.split('/');
      return parts[parts.length - 1];
    };
    
    // Also get products from SelectionManager (includes synced suggested materials)
    const selectionManagerProducts = window.courseMaterialsPage?.components?.selectionManager?.selectedProducts || [];

    const viewChoice = document.querySelectorAll('.view-more-btn-choice.expanded');

    checkboxes.forEach(checkbox => {
      /** @type {HTMLInputElement} */
      const checkboxElement = /** @type {HTMLInputElement} */ (checkbox);
      const courseCard = checkboxElement.closest('.course-card');
      // Checkbox has no course data (html-renderer doesn't set it). Fall back to course card dataset from product-display-manager.
      const campus = checkboxElement.dataset.campus || courseCard?.dataset?.campus || '';
      const term = checkboxElement.dataset.term || courseCard?.dataset?.term || '';
      const termId =
        checkboxElement.dataset.termId || courseCard?.dataset?.termId || '';
      const division = checkboxElement.dataset.division || courseCard?.dataset?.division || '';
      const department = checkboxElement.dataset.department || courseCard?.dataset?.department || '';
      const course = checkboxElement.dataset.course || courseCard?.dataset?.course || '';
      const section = checkboxElement.dataset.section || courseCard?.dataset?.section || '';
      const productId = checkboxElement.dataset.productId;
      const purchaseType = checkboxElement.dataset.purchaseType;
      const variantId = checkboxElement.dataset.variantId;
      const productTitle = checkboxElement.dataset.productTitle;
      const bookISBN = checkboxElement.dataset.bookIsbn;
      const bookEdition = checkboxElement.dataset.bookEdition;
      const bookCopyright = checkboxElement.dataset.bookCopyright;
      const bookPublisher = checkboxElement.dataset.bookPublisher;
      const bookRentDueDate = checkboxElement.dataset.bookRentDueDate;

      const format = checkboxElement.dataset.format;
      const type = checkboxElement.dataset.type;

      const departmentData = checkboxElement.dataset.departmentTaxonomy;
      const subDepartment = checkboxElement.dataset.subDepartment;
      const classds = checkboxElement.dataset.class;
      const subClass = checkboxElement.dataset.subClass;

      const rentalOnlySku= checkboxElement.dataset.rentalOnlySku;

      const subscriptionLength = checkboxElement.dataset.subscriptionLength;

      const materialItem = checkboxElement.closest('.material-item');
      /** @type {HTMLElement|null} */
      const materialElement = /** @type {HTMLElement|null} */ (materialItem);
      const selectedFormat = materialElement
        ? this.getSelectedFormat(materialElement)
        : null;

      // Check if this is a simple product (determined by html-renderer based on variant data)
      const formatBtns = materialElement?.querySelectorAll('.format-btn') || [];
      const isSimple = formatBtns.length === 0;

      // Simple products only need productId & purchaseType, complex products also need selectedFormat
      const hasRequiredData = isSimple
        ? productId && purchaseType
        : selectedFormat && productId && purchaseType;

      if (hasRequiredData) {
        // @ts-ignore
        const product = window.courseData.products_Materials.find(
          /** @param {any} p */ p => p.id == productId
        );

        // For simple products, use variant ID directly. For complex products, use format-based lookup
        const variant = isSimple
          ? product?.variants.find(/** @param {any} v */ v => v.id == variantId)
          : product?.variants.find(
              /** @param {any} v */ v => v.id == variantId
            // @ts-ignore
            ) || this.findVariant(productId, selectedFormat, purchaseType);

        if (variant) {
          selectedProducts.push({
            campus,
            term,
            termId,
            division,
            department,
            course,
            section,
            productId,
            productTitle,
            bookISBN,
            bookEdition,
            bookCopyright,
            bookPublisher,
            bookRentDueDate,
            selectedFormat: selectedFormat || 'simple', // Use 'simple' as fallback
            purchaseType,
            variant,
            price: parseFloat(variant.price),
            departmentData,
            subDepartment,
            classds,
            subClass,
            rentalOnlySku,
            subscriptionLength,
            format,
            type
          });
        }
      }
    });
    
    // Add products from SelectionManager (suggested materials and other selections)
    // This includes products selected from the React component
    selectionManagerProducts.forEach(item => {
      // Skip if already added from checkboxes (by variant ID)
      const variantId = extractVariantId(item.variant?.id);
      const alreadyAdded = selectedProducts.some(sp => {
        const spVariantId = extractVariantId(sp.variant?.id);
        return spVariantId === variantId;
      });
      
      if (!alreadyAdded && item.variant) {
        // Get additional data from the product if available
        const product = window.courseData?.products_Materials?.find(
          p => p.id == item.productId
        );
        
        // Extract data from material item if it exists in DOM
        const materialItem = document.querySelector(`[data-product-id="${item.productId}"]`);
        const checkbox = materialItem?.querySelector('.purchase-checkbox:checked');
        const courseCardSm =
          materialItem?.closest('.course-card') ?? checkbox?.closest('.course-card');

        selectedProducts.push({
          campus: checkbox?.dataset.campus || courseCardSm?.dataset?.campus || '',
          term: checkbox?.dataset.term || courseCardSm?.dataset?.term || '',
          termId: checkbox?.dataset.termId || courseCardSm?.dataset?.termId || '',
          division: checkbox?.dataset.division || courseCardSm?.dataset?.division || '',
          department: checkbox?.dataset.department || courseCardSm?.dataset?.department || '',
          course: checkbox?.dataset.course || courseCardSm?.dataset?.course || '',
          section: checkbox?.dataset.section || courseCardSm?.dataset?.section || '',
          productId: item.productId,
          productTitle: item.productTitle,
          bookISBN: product?.metafields?.isbn_ean || product?.metafields?.course_materials?.isbn_ean || '',
          bookEdition: product?.metafields?.edition || product?.metafields?.course_materials?.edition || '',
          bookCopyright: product?.metafields?.copyright_year || product?.metafields?.course_materials?.copyright_year || '',
          bookPublisher: product?.metafields?.publisher_name || product?.metafields?.course_materials?.publisher_name || '',
          bookRentDueDate: checkbox?.dataset.bookRentDueDate || '',
          selectedFormat: item.selectedFormat || 'simple',
          purchaseType: item.purchaseType || 'Buy',
          variant: item.variant,
          price: item.price || parseFloat(item.variant.price) || 0,
          departmentData: checkbox?.dataset.departmentTaxonomy || '',
          subDepartment: checkbox?.dataset.subDepartment || '',
          classds: checkbox?.dataset.class || '',
          subClass: checkbox?.dataset.subClass || '',
          rentalOnlySku: checkbox?.dataset.rentalOnlySku || '',
          subscriptionLength: checkbox?.dataset.subscriptionLength || '',
          format: checkbox?.dataset.format || '',
          type: checkbox?.dataset.type || '',
        });
      }
    });

    if (selectedProducts.length === 0) {
      alert('Please select some products first.');
      return;
    }

    try {
      const digitalItems = [];
      
      for (const item of selectedProducts) {
        /** @type {any} */
        const selectedItem = item;
        
        // Check if item is digital using the metafields data from the product
        // @ts-ignore
        const product = window.courseData.products_Materials.find(p => p.id == selectedItem.productId);
        
        // Safely check for digital status in metafields
        let isDigital = false;
        let isRental = false;
        let publisherCode = '';
        
        if (product) {
          // Check if metafields exist and have the variant digital status
          if (product.metafields?.custom?.variant_digital_status) {
            if(product.metafields.custom.variant_digital_status[selectedItem.variant.id.split("/").pop()] === true || product.metafields.custom.variant_digital_status[selectedItem.variant.id.split("/").pop()] === "true")
              isDigital = true
          }
          
          // Get publisher code if available
          publisherCode = product.metafields?.course_materials?.publisher_code || '';

          isRental = selectedItem.variant.metafields.find(mf => mf.is_rental !== undefined && mf.namespace === 'cm_rental')?.is_rental ?? false;
          
          
        }
        
        if (isDigital) {
          digitalItems.push({
            ...product,
            publisherCode
          });
        }
        
        const originalDate = selectedItem.bookRentDueDate; 
        const [month, day, year] = originalDate.split('/'); 
        const formattedDate = `${year}-${month}-${day}`; 

        const variantTitle = selectedItem.variant.title.toLowerCase();
        let processing_price_nonreturn = selectedItem.variant.metafields.find(mf => mf.processing_price_nonreturn !== undefined && mf.namespace === 'cm_rental')?.processing_price_nonreturn ?? 0;
        let non_return_charge = 0
        if (variantTitle.includes('new')) {
          non_return_charge = selectedItem.variant.metafields.find(mf => mf.new_price_nonreturn !== undefined && mf.namespace === 'cm_rental')?.new_price_nonreturn ?? 0;
        }
        if (variantTitle.includes('used')) {
          non_return_charge = selectedItem.variant.metafields.find(mf => mf.used_price_nonreturn !== undefined && mf.namespace === 'cm_rental')?.used_price_nonreturn ?? 0;
        }
        // Add digital marker to properties if this is a digital item
        let properties = {};
        if (item.rentalOnlySku === 'true') {
          properties['_CM_Rental_Only_SKU'] = item.rentalOnlySku;
        }
        properties['Course Material'] = 'true';
        properties['Format'] = selectedItem.format || '';
        properties['Type'] = selectedItem.type || '';
        properties['Edition'] = selectedItem.bookEdition || '';
        properties['Campus'] = selectedItem.campus || '';
        properties['Term'] = selectedItem.term || '';
        properties['Division'] = selectedItem.division || '';
        properties['Department'] = selectedItem.department || '';
        properties['Course'] = selectedItem.course || '';
        properties['Section'] = selectedItem.section || '';
        properties['ISBN'] = selectedItem.bookISBN || '';

        const purchaseTypeStr = String(selectedItem.purchaseType || '');
        const typeLabelStr = String(selectedItem.type || '');
        const isBuyType = /buy/i.test(purchaseTypeStr) || /buy/i.test(typeLabelStr);

        // Campus/term rental refund fields apply to rentals only, not buy (print or digital).
        if (!isBuyType) {
          const rentalRefundFromCourseData = getCmrpRentalCampusTermProperties(
            selectedItem.campus || '',
            selectedItem.term || '',
            selectedItem.termId || ''
          );
          properties['_rental_refund_grace_period'] = rentalRefundFromCourseData._rental_refund_grace_period;
          properties['_rental_refund_date'] = rentalRefundFromCourseData._rental_refund_date;
        }

        if (isDigital) {
          // @ts-ignore
          properties['_is_digital'] = 'true';
          // @ts-ignore
          properties['_publisher_code'] = publisherCode || '';

          if (selectedItem.subscriptionLength && selectedItem.subscriptionLength !== ''  && selectedItem.subscriptionLength !== '999' && selectedItem.subscriptionLength !== '0') {              
            properties['Rental Duration'] = selectedItem.subscriptionLength + " days";
          }
        }

        if(isRental) {
          // @ts-ignore
          properties['_rental_flag'] = true;
          // @ts-ignore
          //properties['Is Rental'] = isRental ? 'true' : 'false';
          properties['_non_return_processing_fee'] = processing_price_nonreturn;
          properties['_non_return_charge'] = non_return_charge;

          const rentalCampusTerm = getCmrpRentalCampusTermProperties(
            selectedItem.campus || '',
            selectedItem.term || '',
            selectedItem.termId || ''
          );
          properties['_rental_grace_period'] = rentalCampusTerm._rental_grace_period;
          properties['_rental_refund_date'] = rentalCampusTerm._rental_refund_date;

          if (selectedItem.bookRentDueDate && selectedItem.bookRentDueDate !== '') {
            properties['_rental_expiration_date'] = formattedDate;
            const dateParts = selectedItem.bookRentDueDate.split('/');
            const year = dateParts[2].slice(-2);
            const formattedDateYear = `${dateParts[0]}/${dateParts[1]}/${year}`;
            properties['Rent Due Date'] = formattedDateYear;
          }
        }

        if (selectedItem.departmentData) {
          properties['_department'] = selectedItem.departmentData;
        }
        if (selectedItem.subDepartment) {
          properties['_sub_department'] = selectedItem.subDepartment;
        }
        if (selectedItem.classds) {
          properties['_class'] = selectedItem.classds;
        }
        if (selectedItem.subClass) {
          properties['_sub_class'] = selectedItem.subClass;
          window.appendSubClassCartAudit?.(properties['_sub_class'], 'product-facade.js', 599);
        }
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: selectedItem.variant.id.split("/").pop(),
            quantity: 1,
            properties: properties,
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.message || 'Failed to add item to cart');
        }
      }

      // Add digital delivery fees for all digital items
      if (digitalItems.length > 0) {
        await this.#addDigitalDeliveryFees(digitalItems);
      }

      // Clear selections and update UI
      checkboxes.forEach(cb => {
        /** @type {HTMLInputElement} */
        const checkbox = /** @type {HTMLInputElement} */ (cb);
        checkbox.checked = false;
      });   
     
      if (viewChoice && viewChoice.length > 0) {         
        viewChoice.forEach(viewMoreBtnChoice => {         
          viewMoreBtnChoice.click();
        });
      }
      

      // Clear SelectionManager selections
      if (window.courseMaterialsPage?.components?.selectionManager) {
        window.courseMaterialsPage.components.selectionManager.clearSelections();
      }

      // Clear React component selections (suggested materials)
      if (typeof window !== 'undefined' && window.selectedSuggestedMaterials) {
        window.selectedSuggestedMaterials = [];
        // Notify listeners of the change
        if (window.selectedSuggestedMaterialsListeners) {
          window.selectedSuggestedMaterialsListeners.forEach(listener => {
            try {
              listener();
            } catch (e) {
              console.error('Error in selection change listener:', e);
            }
          });
        }
      }

      const selectionBar = document.querySelector('selection-bar-component');
      if (selectionBar) {
        /** @type {any} */
        const selectionBarElement = selectionBar;
        // Use the existing initializeState method to properly reset the selection bar
        if (selectionBarElement.initializeState) {
          selectionBarElement.initializeState();
        } else {
          // Fallback to manual reset
          selectionBarElement.currentSelectedCount = 0;
          selectionBarElement.currentSelectedTotal = '0.00';
          selectionBarElement.updateVisibility();
        }
      }

      // Get cart data and dispatch update
      const cartData = await fetch('/cart.js').then(r => r.json());
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: 'course-materials',
            data: {
              itemCount: cartData.item_count,
              source: 'course-materials',
            },
          },
        })
      );

      // Check if we're on course materials page - if so, don't open cart drawer
      const isCourseMaterialsPage = document.body.classList.contains('course-materials-page') || 
                                   window.location.pathname.includes('courses-materials');
      
      if (isCourseMaterialsPage) {
        // For course materials page, just redirect to cart without opening drawer
        //window.location.href = '/cart';
      } else {
        // For other pages, open cart drawer as usual
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer) {
          /** @type {any} */
          const cartDrawerElement = cartDrawer;
          if (cartDrawerElement.open) {
            cartDrawerElement.open();
          } else {
            //window.location.href = '/cart';
          }
        } else {
          //window.location.href = '/cart';
        }
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
      alert('Error adding products to cart: ' + error.message);
    }
  }

  /**
   * @param {HTMLElement} materialItem - Material item element
   * @returns {string|null} Selected format
   */
  getSelectedFormat(materialItem) {
    const selectedBtn = materialItem?.querySelector('.format-btn.selected');
    return selectedBtn ? selectedBtn.getAttribute('data-format') : null;
  }

  /**
   * @param {string} productId - Product ID
   * @param {string} selectedFormat - Selected format
   * @param {string} purchaseType - Purchase type
   * @returns {Object} Variant object
   */
  findVariant(productId, selectedFormat, purchaseType) {
    const product = window.courseData.products_Materials.find(
      /** @param {any} p */ p => p.id == productId
    );
    if (!product) throw new Error('Product not found');

    const variant = this.priceCalculator.getVariantByOptions(
      product,
      selectedFormat,
      purchaseType
    );

    if (!variant) {
      throw new Error(
        `Variant not found for ${selectedFormat}/${purchaseType}`
      );
    }
    return variant;
  }
  async addNewAttributeForFee(cart, VARIANT_ID , value) {
    if (!VARIANT_ID) {
      return;
    }
    try {
      // 2. Find the line item by variant_id or line item key
      const lineItem = cart.items.find(item => item.variant_id === parseInt(VARIANT_ID));

      if (!lineItem) {
        console.error('Line item not found');
        return;
      }

      // 3. Merge existing properties with the new one
      const newProperties = {
        ...lineItem.properties, // existing properties
        _requires_digital_delivery_fee: value // new property
      };

      // 4. Update the line item with merged properties
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineItem.key, // use the line item key for precise targeting
          properties: newProperties
        })
      });
      if (response.ok) {
        return true;
      } else {
        const error = await response.json().catch(() => ({}));
        console.error('Failed to remove collateral product:', error);
      }
    } catch (error) {
      console.error('Error removing collateral product:', error);
    }
  }
  /**
   * Add digital delivery fees for selected digital items
   * @param {Array} digitalItems - Array of digital items
   */
  async #addDigitalDeliveryFees(digitalItems) {
    // 1. Fetch the cart
    const responsedata = await fetch('/cart.js');
    const cart = await responsedata.json();
    // Group digital items by publisher to determine fee logic
    //const publisherGroups = this.#groupDigitalItemsByPublisher(digitalItems);
    
    for (const item of digitalItems) {
      const foundPublisherCode = item.variants[0].metafields.find(metafield => 
        metafield.namespace === "course_materials" && metafield.publisher_code
      );

      const publisherCode = foundPublisherCode ? foundPublisherCode.publisher_code : 'default';

      if (await this.#shouldApplyFeeForPublisher(publisherCode)) {
        await this.addNewAttributeForFee(cart, item.variants[0].id.split('/').pop(), 'yes')
        await this.#addFeeForPublisher(1, publisherCode);
      } else {
        await this.addNewAttributeForFee(cart, item.variants[0].id.split('/').pop(), 'no')
      }
    }
  }

  /**
   * Group digital items by publisher code
   * @param {Array} digitalItems - Digital items
   * @returns {Object} Grouped items by publisher
   */
  #groupDigitalItemsByPublisher(digitalItems) {
    const groups = {};
    
    digitalItems.forEach(item => {
      const foundPublisherCode = item.variants[0].metafields.find(item => item.namespace === "course_materials" && item.publisher_code);
      const publisherCode = foundPublisherCode ? foundPublisherCode.publisher_code : 'default'
      // @ts-ignore
      if (!groups[publisherCode]) {
        // @ts-ignore
        groups[publisherCode] = [];
      }
      // @ts-ignore
      groups[publisherCode].push(item);
    });
    
    return groups;
  }

  /**
   * Check if fee should be applied for publisher
   * @param {Object} config - Digital fee config
   * @param {string} publisherCode - Publisher code
   * @returns {boolean}
   */
  async #shouldApplyFeeForPublisher(publisherCode) {
    const api_url = window.digital_product_config?.api_url ?? ''
    const store_number = window.digital_product_config?.store_number ?? ''

    if(api_url && store_number) {
      try {
        const apiResponse = await fetch(`${api_url}?storeNumber=${store_number}&downloadprovidercode=${publisherCode}`)
        const result = await apiResponse.json();
        if(result && result.length > 0 && result[0].isApplied) {
          return true;
        } else {
          return false
        }
      } catch (error) {
        console.error(error);
      }
      
    } else {
      // Digital fee config not found
    }
  }
  async fetchMetafields(variantId) {
    if (typeof window !== 'undefined' && window.fetchMetafieldsOnce) {
      return window.fetchMetafieldsOnce(variantId);
    }

    const proxyPath = '/apps/shopifyData/getMetafields';
    const url = `${proxyPath}?query=gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Metafields');
      }
    } catch (error) {
      console.error('Error fetching Metafields:', error);
      throw error;
    }
  }
  /**
   * Add fee for specific publisher
   * @param {number} quantity - Quantity of items
   */
  // @ts-ignore
  async #addFeeForPublisher(quantity, feePublisherCode) {
    try {
      
      const feeProductHandle = window.digital_product_config?.product_handle ?? '';
      
      // Get the fee product to find its variant ID
      const productResponse = await fetch(`/products/${feeProductHandle}.js`);
      if (!productResponse.ok) {
        console.error('Digital delivery fee product not found');
        return;
      }
      
      const feeProduct = await productResponse.json();
      const feeVariantId = feeProduct.variants[0]?.id;
      
      if (!feeVariantId) {
        console.error('No variant found for digital delivery fee product');
        return;
      }
      const metafields = await this.fetchMetafields(feeVariantId)
      const getMetafieldValue = (key) => {
          const metafield = metafields.find(m => m.key === key);
          return metafield ? metafield.value : ''; 
      };
      const feeSubClass = getMetafieldValue('sub_class');
      // Add the fee product to cart
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: feeVariantId,
          quantity: quantity,
          properties: {
            '_digital_delivery_fee': 'true',
            '_is_digital': 'true',
            '_fee_type': 'online_store',
            '_department': getMetafieldValue('department'), 
            '_sub_department': getMetafieldValue('sub_department'), 
            '_class': getMetafieldValue('class'), 
            '_sub_class': feeSubClass
          }
        })
      });

      window.appendSubClassCartAudit?.(feeSubClass, 'product-facade.js', 944);
      
    } catch (error) {
      console.error('Failed to add digital delivery fee:', error);
    }
  }

  /**
   * @param {string} variantId - Variant ID
   * @param {string} productTitle - Product title
   * @param {string} purchaseType - Purchase type
   */
  handleNotifyMe(variantId, productTitle, purchaseType) {
    // @ts-ignore
    if (typeof window.openNotifyMeDialog === 'function') {
      // @ts-ignore
      window.openNotifyMeDialog(variantId, productTitle, purchaseType);
    } else {
      alert(
        `We'll notify you when "${productTitle}" (${purchaseType}) is back in stock!`
      );
    }
  }

  // Factory method
  /**
   * @param {Product[]} products - Products array
   * @param {any[]} ddcsSection - DDCS section array
   * @returns {SOLIDProductHandler} Handler instance
   */
  static createForShopify(products, ddcsSection) {
    return new SOLIDProductHandler({
      products,
      ddcsSection,
      // @ts-ignore
      variantDetector: new window.ProductSystem.ShopifyVariantDetector(),
      // @ts-ignore
      infoExtractor: new window.ProductSystem.ShopifyInfoExtractor(),
      // @ts-ignore
      priceCalculator: new window.ProductSystem.ShopifyPriceCalculator(),
      // @ts-ignore
      htmlRenderer: new window.ProductSystem.ShopifyHTMLRenderer(
        // @ts-ignore
        new window.ProductSystem.ShopifyVariantDetector(),
        // @ts-ignore
        new window.ProductSystem.ShopifyPriceCalculator()
      ),
      // @ts-ignore
      adoptionManager: new window.ProductSystem.AdoptionStatusManager(),
    });
  }
}

// @ts-ignore
window.ProductSystem = window.ProductSystem || {};
// @ts-ignore
window.ProductSystem.SOLIDProductHandler = SOLIDProductHandler;